// Import React library for creating components
import React from 'react';
// Import routing components: Link for navigation, useLocation to get current page
import { Link, useLocation } from 'react-router-dom';
// Import icon components from Lucide React icon library for the navigation menu
import { LayoutGrid, Calendar, Settings, Folder, FolderOpen, BarChart2, Building2, Inbox, FileCheck, CalendarClock } from 'lucide-react';
// Import custom components used in the layout
import { InstallButton } from '../InstallButton'; // Button to install the app as PWA
import { UserProfileDropdown } from '../UserProfileDropdown'; // User menu in top-right
import { NotificationBadge } from '../notifications/NotificationBadge'; // Red badge showing notification counts
import { useNotifications } from '../../contexts/NotificationContext'; // Hook to get notification data

// Define the shape of props (properties) this component expects
// TypeScript interface ensures we pass the right data to this component
interface AppLayoutProps {
  // children: The page content that will be displayed inside this layout
  // React.ReactNode can be any valid React content (text, components, HTML, etc.)
  children: React.ReactNode;
}

// AppLayout is the main desktop/tablet layout component with navigation sidebar
// It wraps around page content and provides consistent navigation across the app
function AppLayout({ children }: AppLayoutProps) {
  // useLocation gives us information about the current page URL
  // We use location.pathname to highlight the active navigation item
  const location = useLocation();
  // Get the count of overdue notifications from the notification system
  // overdueCount will be used to show a red badge on the Deadlines menu item
  const { overdueCount } = useNotifications();

  // Define the main navigation menu items
  // Each item has a name (display text), href (URL path), and icon (visual indicator)
  const navigation = [
    { name: 'Overview', href: '/overview', icon: BarChart2 }, // Dashboard with statistics
    { name: 'Clients', href: '/clients', icon: Building2 }, // Client management page
    { name: 'Projects', href: '/projects', icon: FolderOpen }, // Project management
    { name: 'Matters', href: '/matters', icon: Folder }, // Legal matters/cases
    { name: 'Deliverables', href: '/dashboard', icon: LayoutGrid }, // Goals and tasks
    { name: 'Deadlines', href: '/deadlines', icon: CalendarClock }, // Upcoming deadlines
    { name: 'Regular Tasks', href: '/daily', icon: Calendar }, // Recurring daily tasks
    { name: 'Inbox', href: '/inbox', icon: Inbox }, // Task inbox for quick capture
    { name: 'Settings', href: '/settings', icon: Settings }, // App configuration
  ];

  // Render the layout structure
  return (
    // Main container: full screen height, gray background, flex column layout
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Top Navigation Bar */}
      {/* bg-white: white background, shadow-sm: subtle shadow, flex-shrink-0: don't shrink when space is limited */}
      <nav className="bg-white shadow-sm flex-shrink-0">
        {/* Navigation container with responsive padding and max width */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main navigation flex container - 16 units tall (64px) */}
          <div className="flex justify-between h-16">
            {/* Left side of navigation */}
            <div className="flex">
              {/* Logo/Brand section */}
              <div className="flex-shrink-0 flex items-center">
                {/* App logo/name that links to overview page */}
                <Link to="/overview" className="text-xl font-bold text-gray-900 hover:text-gray-700 transition-colors">
                  Goalie4
                </Link>
              </div>
              {/* Desktop navigation menu - hidden on mobile (sm:flex), with spacing */}
              <div className="hidden sm:ml-8 sm:flex sm:space-x-8">
                {/* Loop through each navigation item and create a link */}
                {navigation.map((item) => {
                  // Check if this navigation item matches the current page URL
                  const isActive = location.pathname === item.href;
                  return (
                    // Create a navigation link with conditional styling
                    <Link
                      key={item.name} // React key for list items
                      to={item.href} // URL to navigate to
                      // Dynamic CSS classes based on whether this is the active page
                      className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                        isActive
                          ? 'border-indigo-500 text-gray-900' // Active: blue bottom border, dark text
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700' // Inactive: transparent border, gray text
                      }`}
                    >
                      {/* Render the icon for this navigation item */}
                      <item.icon className="w-4 h-4 mr-2" />
                      {/* Display the name of the navigation item */}
                      {item.name}
                      {/* Show notification badge on Deadlines menu if there are overdue items */}
                      {item.name === 'Deadlines' && overdueCount > 0 && (
                        <NotificationBadge count={overdueCount} className="ml-2" />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
            {/* Right side of navigation */}
            <div className="flex items-center space-x-4">
              {/* Button to install the app as a Progressive Web App (PWA) */}
              <InstallButton />
              {/* User profile dropdown menu with account options */}
              <UserProfileDropdown />
            </div>
          </div>
        </div>
        
        {/* Mobile navigation menu - only visible on small screens */}
        {/* sm:hidden means this section is hidden on screens larger than mobile */}
        <div className="sm:hidden">
          <div className="pt-2 pb-3 space-y-1">
            {/* Loop through navigation items to create mobile menu */}
            {navigation.map((item) => {
              // Check if this item is the currently active page
              const isActive = location.pathname === item.href;
              return (
                // Mobile navigation link with left border instead of bottom border
                <Link
                  key={item.name}
                  to={item.href}
                  // Mobile styling: block display, left border for active state
                  className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                    isActive
                      ? 'bg-indigo-50 border-indigo-500 text-indigo-700' // Active: background color, blue left border
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700' // Inactive: transparent border, hover effects
                  }`}
                >
                  {/* Mobile navigation item layout */}
                  <div className="flex items-center justify-between w-full">
                    {/* Left side: icon and name */}
                    <div className="flex items-center">
                      <item.icon className="w-4 h-4 mr-3" />
                      {item.name}
                    </div>
                    {/* Right side: notification badge if needed */}
                    {item.name === 'Deadlines' && overdueCount > 0 && (
                      <NotificationBadge count={overdueCount} className="mr-2" />
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main content area */}
      {/* flex-1: takes up remaining vertical space, overflow-y-auto: scrollable if content is tall */}
      {/* pb-16: padding bottom to prevent content being hidden behind mobile navigation */}
      <main className="flex-1 overflow-y-auto pb-16">{children}</main>
    </div>
  );
}

// Export AppLayout as the default export so other files can import it
export default AppLayout;
